// =============================================================================
// Stripe Terminal (Wise/BBPOS card-present) integration.
//
// The Stripe Terminal JS SDK must be loaded from js.stripe.com (PCI), so we
// inject it on demand rather than bundling an npm package. Card-present
// payments also REQUIRE a small backend (two endpoints), because a secret key
// must never live in the browser:
//
//   1. Connection-token endpoint  -> POST returns { secret }
//        (server calls stripe.terminal.connectionTokens.create)
//   2. PaymentIntent endpoint     -> POST { amount, currency, description }
//        returns { client_secret } (server creates a card_present PaymentIntent
//        with capture_method:'automatic')
//
// Configure via .env:
//   VITE_STRIPE_TERMINAL_TOKEN_URL   = https://<edge>/stripe-connection-token
//   VITE_STRIPE_TERMINAL_INTENT_URL  = https://<edge>/stripe-create-intent
//   VITE_STRIPE_TERMINAL_SIMULATED   = true        (optional, for testing)
//   VITE_STRIPE_TERMINAL_LOCATION    = tml_xxx      (optional reader location)
//
// Supabase edge-function stubs for the two endpoints live in
// supabase/functions/stripe-terminal/. Until the endpoints are configured,
// isStripeTerminalConfigured() returns false and the UI hides the card-terminal
// option (so nothing breaks without Stripe set up).
// =============================================================================

/* eslint-disable @typescript-eslint/no-explicit-any */

const TOKEN_URL = import.meta.env.VITE_STRIPE_TERMINAL_TOKEN_URL as string | undefined
const INTENT_URL = import.meta.env.VITE_STRIPE_TERMINAL_INTENT_URL as string | undefined
const SIMULATED = String(import.meta.env.VITE_STRIPE_TERMINAL_SIMULATED ?? '') === 'true'
const LOCATION = import.meta.env.VITE_STRIPE_TERMINAL_LOCATION as string | undefined
// Server-driven Reader MOTO endpoint + reader id (legacy "Manual Stripe" path).
const READER_PROCESS_URL = import.meta.env.VITE_STRIPE_READER_PROCESS_URL as string | undefined
const READER_ID = import.meta.env.VITE_STRIPE_TERMINAL_ID as string | undefined
const SDK_URL = 'https://js.stripe.com/terminal/v1/'

export function isStripeTerminalConfigured(): boolean {
  return Boolean(TOKEN_URL && INTENT_URL)
}

// MOTO (mail-order/telephone-order) needs only the server-driven reader endpoint;
// it does NOT use the browser Terminal SDK, mirroring the legacy POS.
export function isStripeMotoConfigured(): boolean {
  return Boolean(READER_PROCESS_URL)
}

let sdkPromise: Promise<any> | null = null
function loadSdk(): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'))
  if ((window as any).StripeTerminal) return Promise.resolve((window as any).StripeTerminal)
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).StripeTerminal))
      existing.addEventListener('error', () => reject(new Error('Failed to load Stripe Terminal SDK')))
      return
    }
    const s = document.createElement('script')
    s.src = SDK_URL
    s.async = true
    s.onload = () => resolve((window as any).StripeTerminal)
    s.onerror = () => reject(new Error('Failed to load Stripe Terminal SDK'))
    document.head.appendChild(s)
  })
  return sdkPromise
}

async function fetchConnectionToken(): Promise<string> {
  const res = await fetch(TOKEN_URL as string, { method: 'POST' })
  if (!res.ok) throw new Error(`Connection token request failed (${res.status})`)
  const json = await res.json()
  if (!json.secret) throw new Error('Connection token endpoint did not return a secret')
  return json.secret
}

async function createPaymentIntent(amountCents: number, description?: string): Promise<string> {
  const res = await fetch(INTENT_URL as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount: amountCents, currency: 'usd', description }),
  })
  if (!res.ok) throw new Error(`PaymentIntent request failed (${res.status})`)
  const json = await res.json()
  if (!json.client_secret) throw new Error('Intent endpoint did not return a client_secret')
  return json.client_secret
}

let terminal: any = null
let connectedReader: any = null

async function getTerminal(): Promise<any> {
  if (terminal) return terminal
  const StripeTerminal = await loadSdk()
  terminal = StripeTerminal.create({
    onFetchConnectionToken: fetchConnectionToken,
    onUnexpectedReaderDisconnect: () => { connectedReader = null },
  })
  return terminal
}

async function ensureReader(opts?: ChargeOptions): Promise<any> {
  // If already connected but to a different reader than requested, drop it.
  if (connectedReader && opts?.readerId && connectedReader.id !== opts.readerId) {
    try { await terminal?.disconnectReader() } catch { /* ignore */ }
    connectedReader = null
  }
  if (connectedReader) return connectedReader
  const t = await getTerminal()
  const config: any = { simulated: SIMULATED }
  const location = opts?.locationId || LOCATION
  if (location) config.location = location
  const discovered = await t.discoverReaders(config)
  if (discovered.error) throw new Error(discovered.error.message)
  if (!discovered.discoveredReaders?.length) throw new Error('No card readers found')
  // Prefer the register's assigned reader id when provided; else first found.
  const target = opts?.readerId
    ? discovered.discoveredReaders.find((r: any) => r.id === opts.readerId || r.label === opts.readerId)
    : null
  const reader = target ?? discovered.discoveredReaders[0]
  if (opts?.readerId && !target) {
    throw new Error(`Assigned reader ${opts.readerId} for this register was not found among connected readers`)
  }
  const connect = await t.connectReader(reader)
  if (connect.error) throw new Error(connect.error.message)
  connectedReader = connect.reader
  return connectedReader
}

// Per-register reader/location override (looked up from the registers table).
export interface ChargeOptions {
  readerId?: string | null
  locationId?: string | null
}

export interface TerminalResult {
  status: 'succeeded' | 'requires_capture' | string
  paymentIntentId: string
}

// Run a full card-present charge: connect a reader (cached), create the intent,
// collect the card, and process it. Throws on any failure so the caller can
// surface the error and keep the sale open.
export async function chargeOnTerminal(amountCents: number, description?: string, opts?: ChargeOptions): Promise<TerminalResult> {
  if (!isStripeTerminalConfigured()) throw new Error('Stripe Terminal is not configured')
  const t = await getTerminal()
  await ensureReader(opts)

  const clientSecret = await createPaymentIntent(amountCents, description)
  const collect = await t.collectPaymentMethod(clientSecret)
  if (collect.error) throw new Error(collect.error.message)

  const process = await t.processPayment(collect.paymentIntent)
  if (process.error) throw new Error(process.error.message)

  const pi = process.paymentIntent
  return { status: pi.status, paymentIntentId: pi.id }
}

// MOTO / "Manual Stripe" charge — faithful port of the legacy server-driven
// reader flow. The clerk keys the card on the physical terminal keypad; the
// backend sets process_config[moto]=true, polls the reader, and captures.
// Returns cardType "Phone" when the charge is not card-present (legacy behavior).
export interface MotoResult extends TerminalResult {
  cardType: string
}

export async function chargeMotoOnReader(amountCents: number, description?: string, opts?: ChargeOptions): Promise<MotoResult> {
  if (!isStripeMotoConfigured()) throw new Error('Stripe MOTO (Manual Stripe) is not configured')
  const res = await fetch(READER_PROCESS_URL as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount: amountCents, currency: 'usd', description, readerId: opts?.readerId || READER_ID }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || `MOTO request failed (${res.status})`)
  return {
    status: json.status,
    paymentIntentId: json.paymentIntentId,
    cardType: json.cardType ?? 'Phone',
  }
}

export async function disconnectReader(): Promise<void> {
  if (terminal && connectedReader) {
    try { await terminal.disconnectReader() } catch { /* ignore */ }
    connectedReader = null
  }
}
